import { c, t } from "ttag";

import { CollapseSection } from "metabase/common/components/CollapseSection";
import { useSelector } from "metabase/redux";
import { getEntityTypes } from "metabase/redux/embedding-data-picker";
import { useUserSetting } from "metabase/settings";
import { ActionIcon, Icon, Tooltip } from "metabase/ui";
import { isWithinIframe } from "metabase/utils/iframe";

import { SidebarHeading } from "../Sidebar";
import { SidebarLink } from "../SidebarItems";
import { trackAddDataModalOpened } from "../analytics";
import type { SelectedItem } from "../types";

import { useCanAddData } from "./use-can-add-data";

export const BrowseNavSection = ({
  nonEntityItem,
  onItemSelect,
  onAddDataModalOpen,
  hasDataAccess,
}: {
  nonEntityItem: SelectedItem;
  onItemSelect: () => void;
  onAddDataModalOpen: () => void;
  hasDataAccess: boolean;
}) => {
  const BROWSE_MODELS_URL = "/browse/models";
  const BROWSE_DATA_URL = "/browse/databases";
  const BROWSE_METRICS_URL = "/browse/metrics";

  const [expandBrowse = true, setExpandBrowse] = useUserSetting(
    "expand-browse-in-nav",
  );

  const canAddData = useCanAddData();
  const entityTypes = useSelector(getEntityTypes);
  const isEmbeddingIframe = isWithinIframe();

  const showAddDataButton = canAddData && !isEmbeddingIframe;

  return (
    <CollapseSection
      header={
        <SidebarHeading>
          {c("A noun, shown in the sidebar as a navigation link").t`Data`}
        </SidebarHeading>
      }
      initialState={expandBrowse ? "expanded" : "collapsed"}
      iconPosition="right"
      iconSize={8}
      onToggle={setExpandBrowse}
      rightAction={
        showAddDataButton ? (
          <Tooltip label={t`Add data`}>
            <ActionIcon
              aria-label={t`Add data`}
              color="text-secondary"
              onClick={() => {
                trackAddDataModalOpened("left-nav");
                onAddDataModalOpen();
              }}
            >
              <Icon name="add" />
            </ActionIcon>
          </Tooltip>
        ) : undefined
      }
      role="section"
      aria-label={t`Data`}
    >
      {hasDataAccess &&
        (!isEmbeddingIframe || entityTypes.includes("table")) && (
          <SidebarLink
            icon="database"
            url={BROWSE_DATA_URL}
            isSelected={nonEntityItem?.url?.startsWith(BROWSE_DATA_URL)}
            onClick={onItemSelect}
            aria-label={t`Browse databases`}
          >
            {t`Databases`}
          </SidebarLink>
        )}

      {(!isEmbeddingIframe || entityTypes.includes("model")) && (
        <SidebarLink
          icon="model"
          url={BROWSE_MODELS_URL}
          isSelected={nonEntityItem?.url?.startsWith(BROWSE_MODELS_URL)}
          onClick={onItemSelect}
          aria-label={t`Browse models`}
        >
          {t`Models`}
        </SidebarLink>
      )}

      {!isEmbeddingIframe && (
        <SidebarLink
          icon="metric"
          url={BROWSE_METRICS_URL}
          isSelected={nonEntityItem?.url?.startsWith(BROWSE_METRICS_URL)}
          onClick={onItemSelect}
          aria-label={t`Browse metrics`}
        >
          {t`Metrics`}
        </SidebarLink>
      )}
    </CollapseSection>
  );
};
